import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { usePortalResume } from '../hooks/usePortalResume';

/** Portal-wide defaults: same gates as the meal-plan workflow (heavy fetches). */
export const PORTAL_RESUME_MIN_HIDDEN_MS = 10_000;
export const PORTAL_RESUME_STALE_MS = 45_000;

type PortalResumeContextValue = {
  /** Increments when tab resume passes min-hidden OR stale-time (see usePortalResume). */
  generation: number;
};

const PortalResumeContext = createContext<PortalResumeContextValue | null>(null);

export function PortalResumeProvider({ children }: { children: React.ReactNode }) {
  const [generation, setGeneration] = useState(0);

  usePortalResume(
    () => {
      setGeneration((g) => g + 1);
    },
    { minHiddenMs: PORTAL_RESUME_MIN_HIDDEN_MS, staleTimeMs: PORTAL_RESUME_STALE_MS },
  );

  const value = useMemo(() => ({ generation }), [generation]);

  return <PortalResumeContext.Provider value={value}>{children}</PortalResumeContext.Provider>;
}

export function usePortalResumeGeneration(): number {
  const ctx = useContext(PortalResumeContext);
  if (!ctx) {
    throw new Error('usePortalResumeGeneration must be used within PortalResumeProvider');
  }
  return ctx.generation;
}

/**
 * Runs after a portal-wide resume (same rules as Provider). Skips the effect on initial mount
 * so it does not duplicate the page’s first load.
 */
export function useOnPortalResume(callback: () => void | Promise<void>) {
  const generation = usePortalResumeGeneration();
  const cbRef = useRef(callback);
  cbRef.current = callback;
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    void Promise.resolve(cbRef.current());
  }, [generation]);
}
